import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Heart, MessageSquare, Send, Edit2, Trash2 } from 'lucide-react';
import Button from '../Button';
import UserName from '../UserName';
import { useAuthStore } from '../../store/authStore';
import supabase from '../../lib/supabase';

interface InformationHubPostProps {
  post: any;
}

export default function InformationHubPost({ post }: InformationHubPostProps) {
  const { user } = useAuthStore();
  const [replyContent, setReplyContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localPost, setLocalPost] = useState(post);
  const [authorProfile, setAuthorProfile] = useState<any>(null);
  const [expandedPost, setExpandedPost] = useState<string | null>(null);
  const [replies, setReplies] = useState<Record<string, any[]>>({});
  const [newReply, setNewReply] = useState<Record<string, string>>({});
  const [editingReply, setEditingReply] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    const fetchAuthorProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', post.author_id)
          .single();

        if (error) throw error;
        setAuthorProfile(data);
      } catch (error) {
        console.error('Error fetching author profile:', error);
      }
    };

    fetchAuthorProfile();
    fetchReplies(post.id);
  }, [post.author_id, post.id]);

  const fetchReplies = async (postId: string) => {
    try {
      const { data, error } = await supabase
        .from('information_hub_post_replies')
        .select(`
          *,
          author:author_id(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          )
        `)
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setReplies(prev => ({ ...prev, [postId]: data || [] }));
    } catch (err: any) {
      console.error('Error fetching replies:', err);
    }
  };

  const handleLikeInfoPost = async (postId: string) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase.rpc('toggle_info_hub_post_like', {
        post_id: postId,
        user_id: user.id
      });

      if (error) throw error;

      setLocalPost(prev => {
        const userLiked = prev.likes.includes(user.id);
        return {
          ...prev,
          like_count: userLiked ? prev.like_count - 1 : prev.like_count + 1,
          likes: userLiked 
            ? prev.likes.filter((id: string) => id !== user.id)
            : [...prev.likes, user.id]
        };
      });
    } catch (err: any) {
      console.error('Error toggling like:', err);
    }
  };

  const handleLikeReply = async (replyId: string, postId: string) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase.rpc('toggle_info_hub_reply_like', {
        reply_id: replyId,
        user_id: user.id
      });

      if (error) throw error;

      setReplies(prev => ({
        ...prev,
        [postId]: prev[postId].map(reply => {
          if (reply.id === replyId) {
            const userLiked = reply.likes.includes(user.id);
            return {
              ...reply,
              like_count: userLiked ? reply.like_count - 1 : reply.like_count + 1,
              likes: userLiked 
                ? reply.likes.filter(id => id !== user.id)
                : [...reply.likes, user.id]
            };
          }
          return reply;
        })
      }));
    } catch (err: any) {
      console.error('Error toggling like:', err);
    }
  };

  const handleSubmitReply = async (postId: string) => {
    if (!user || !newReply[postId]?.trim()) return;

    try {
      const { error } = await supabase
        .from('information_hub_post_replies')
        .insert({
          post_id: postId,
          author_id: user.id,
          content: newReply[postId].trim()
        });

      if (error) throw error;

      setNewReply(prev => ({ ...prev, [postId]: '' }));
      await fetchReplies(postId);
      
      // Update local post reply count
      setLocalPost(prev => ({
        ...prev,
        reply_count: (prev.reply_count || 0) + 1
      }));
    } catch (err: any) {
      console.error('Error submitting reply:', err);
    }
  };

  const handleEditReply = async (replyId: string, postId: string) => {
    if (!editContent.trim()) return;

    try {
      const { error } = await supabase
        .from('information_hub_post_replies')
        .update({
          content: editContent.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', replyId)
        .eq('author_id', user?.id);

      if (error) throw error;

      setEditingReply(null);
      setEditContent('');
      await fetchReplies(postId);
    } catch (err: any) {
      console.error('Error updating reply:', err);
    }
  };

  const handleDeleteReply = async (replyId: string, postId: string) => {
    if (!window.confirm('Are you sure you want to delete this reply?')) return;

    try {
      const { error } = await supabase
        .from('information_hub_post_replies')
        .delete()
        .eq('id', replyId)
        .eq('author_id', user?.id);

      if (error) throw error;

      await fetchReplies(postId);
      
      // Update local post reply count
      setLocalPost(prev => ({
        ...prev,
        reply_count: Math.max(0, (prev.reply_count || 1) - 1)
      }));
    } catch (err: any) {
      console.error('Error deleting reply:', err);
    }
  };

  const toggleComments = async (postId: string) => {
    if (expandedPost === postId) {
      setExpandedPost(null);
    } else {
      setExpandedPost(postId);
      await fetchReplies(postId);
    }
  };

  return (
    <article className="bg-surface-light rounded-lg">
      <div className="px-2 pt-2 pb-6">
        <header>
          <h1 className="text-4xl font-bold text-primary mb-2">{localPost.title}</h1>
          <p className="text-sm text-neutral mb-4">
            Published {format(new Date(localPost.updated_at), 'MMMM d, yyyy')}
          </p>
        </header>

        {localPost.cover_image && (
          <img
            src={localPost.cover_image}
            alt={localPost.title}
            className="w-full rounded-lg mb-6"
          />
        )}

        <div className="flex items-start gap-4 mb-6">
          <img
            src={authorProfile?.avatar_url || `https://ui-avatars.com/api/?name=${authorProfile?.display_name || authorProfile?.full_name}`}
            alt={authorProfile?.display_name || authorProfile?.full_name}
            className="w-16 h-16 rounded-full"
          />
          <div>
            <UserName
              name={authorProfile?.display_name || authorProfile?.full_name}
              verified={authorProfile?.verified}
              userId={authorProfile?.id}
              className='text-primary'
            />
            {authorProfile?.bio && (
              <p className="text-sm text-neutral mt-1">{authorProfile.bio}</p>
            )}
          </div>
        </div>

        <div className="prose prose-neutral max-w-none mb-8 text-neutral whitespace-pre-line">
          {localPost.content}
        </div>

        <div className="flex items-center justify-between text-sm text-neutral group-hover:text-surface-light">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => handleLikeInfoPost(localPost.id)}
              className={`flex items-center space-x-1 hover:text-neutral group-hover:text-surface-light transition-colors ${
                user && localPost.likes?.includes(user.id) ? 'text-neutral group-hover:text-surface-light fill-current' : ''
              }`}
            >
              <Heart 
                className={`w-4 h-4 ${
                  user && localPost.likes?.includes(user.id) ? 'fill-current' : ''
                }`} 
              />
              <span>{localPost.like_count}</span>
            </button>

            <button
              onClick={() => toggleComments(localPost.id)}
              className="flex items-center space-x-1 group-hover:text-surface-light transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              <span>{localPost.reply_count || 0}</span>
            </button>
          </div>
        </div>

        {expandedPost === localPost.id && (
          <div className="mt-4 space-y-4 border-t border-neutral pt-4">
            {replies[localPost.id]?.map(reply => (
              <div key={reply.id} className="bg-primary-dark rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-neutral mb-2 group-hover:text-surface-light">
                    <UserName
                      name={reply.author.display_name || reply.author.full_name}
                      verified={reply.author.verified}
                      size="sm"
                      userId={reply.author.id}
                      className="text-neutral group-hover:text-neutral"
                    />
                  </span>

                  {user?.id === reply.author.id && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setEditingReply(reply.id);
                          setEditContent(reply.content);
                        }}
                        className="p-1 text-primary hover:text-base transition-colors"
                        title="Edit reply"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteReply(reply.id, localPost.id)}
                        className="p-1 text-primary hover:text-primary transition-colors"
                        title="Delete reply"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {editingReply === reply.id ? (
                  <div className="mb-3">
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="text-primary w-full resize-none bg-transparent border-none focus:outline-none focus:ring-0 px-0 py-0 text-sm"
                      rows={1}
                    />
                    <div className="flex justify-end space-x-1 mt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-16 h-16 rounded-full text-s bg-secondary-light border-0 border-transparent text-neutral hover:bg-neutral hover:text-surface-light hover:border-neutral"
                        onClick={() => {
                          setEditingReply(null);
                          setEditContent('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-16 h-16 rounded-full text-s bg-secondary-light border-0 border-transparent text-neutral hover:bg-neutral hover:text-surface-light hover:border-neutral"
                        onClick={() => handleEditReply(reply.id, localPost.id)}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <div className="text-primary mb-3">
                      {reply.content}
                    </div>

                    <div className="flex items-center justify-between text-sm text-primary">
                      <button
                        onClick={() => handleLikeReply(reply.id, localPost.id)}
                        className={`flex items-center space-x-1 hover:text-primary transition-colors ${
                          user && reply.likes?.includes(user.id) ? 'text-primary fill-current' : ''
                        }`}
                      >
                        <Heart 
                          className={`w-4 h-4 ${
                            user && reply.likes?.includes(user.id) ? 'fill-current' : ''
                          }`}
                        />
                        <span>{reply.like_count}</span>
                      </button>
                      <span className="text-sm text-primary">
                        {format(new Date(reply.created_at), 'PPp')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {user && (
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newReply[localPost.id] || ''}
                  onChange={(e) => setNewReply(prev => ({ ...prev, [localPost.id]: e.target.value }))}
                  placeholder="Write a comment..."
                  className="flex-1 pl-4 bg-primary-dark rounded-full px-4 py-2 text-primary placeholder:text-primary focus:outline-none focus:ring-0"
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleSubmitReply(localPost.id)}
                  disabled={!newReply[localPost.id]?.trim()}
                  className="bg-secondary-light text-center border-0 border-transparent text-neutral hover:bg-neutral hover:text-surface-light hover:border-neutral min-w-[60px] px-3 py-1 text-xs flex items-center justify-center gap-1"
                >
                  <Send className="w-3 h-3" />
                  <span>Send</span>
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
